import { useMemo, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "../../Buttons/Button";
import { Views } from "react-big-calendar";
import { useTranslationApp } from "../../../lib/i18next";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { isSameMonth, isSameWeek, isSameDay } from "date-fns";
import { useTheme, IconButton } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import Select from "../../Form/Select";
import { useForm } from "react-hook-form";
import {
  CALENDAR_MONTH,
  CALENDAR_WEEK,
  CALENDAR_DAY,
  CALENDAR_TODAY,
  CALENDAR_NEXT,
  CALENDAR_PREVIOUS,
} from "../../../i18n/keysTranslations";

const CalendarToolbar = (toolbar) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { control, setValue } = useForm({
    defaultValues: {
      view: toolbar.view,
    },
  });

  useEffect(() => {
    setValue("view", toolbar.view);
  }, [toolbar.view, setValue]);

  const goToBack = () => toolbar.onNavigate("PREV");
  const goToNext = () => toolbar.onNavigate("NEXT");
  const goToToday = () => toolbar.onNavigate("TODAY");
  const goToView = (view) => () => toolbar.onView(view);
  const handleViewChange = (value) => {
    toolbar.onView(value);
  };

  const viewOptions = [
    { value: Views.MONTH, label: CALENDAR_MONTH },
    { value: Views.WEEK, label: CALENDAR_WEEK },
    { value: Views.DAY, label: CALENDAR_DAY },
  ];

  const canGoBack = useMemo(() => {
    const today = new Date();
    const currentDate = toolbar.date || today;
    const view = toolbar.view || "week";

    const comparators = {
      month: () => isSameMonth(currentDate, today),
      week: () => isSameWeek(currentDate, today, { weekStartsOn: 1 }),
      day: () => isSameDay(currentDate, today),
    };

    const isCurrent = comparators[view]?.() ?? false;
    return !isCurrent;
  }, [toolbar.date, toolbar.view]);

  const capitalizeWords = (text) => {
    if (!text) return "";
    return text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 2,
          px: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
            {capitalizeWords(toolbar.label)}
          </Typography>
          <IconButton
            onClick={() => toolbar.onCloseModal && toolbar.onCloseModal()}
            sx={{
              p: 0.5,
              backgroundColor: "background.paperSecondary",
              color: "background.paperSecondaryContrastText",
              "&:hover": { backgroundColor: "background.paperSecondary" },
            }}
          >
            <X size={24} />
          </IconButton>
        </Box>

        {/* Fila 2: Navegación y Selector de Vista */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Button
              onClick={goToBack}
              variant="contained"
              color="secondary"
              size="small"
              disabled={!canGoBack}
              sx={{
                minWidth: "auto",
                py: "6px",
                px: "8px !important",
                height: "40px !important",
              }}
            >
              <ChevronLeft size={20} />
            </Button>
            <Button
              onClick={goToToday}
              variant="contained"
              color="secondary"
              size="small"
              sx={{
                py: "6px",
                px: "12px",
                height: "40px !important",
                width: "60px !important",
                minWidth: "60px !important",
              }}
            >
              {t(CALENDAR_TODAY)}
            </Button>
            <Button
              onClick={goToNext}
              variant="contained"
              color="secondary"
              size="small"
              sx={{
                minWidth: "auto",
                py: "6px",
                px: "8px !important",
                height: "40px !important",
              }}
            >
              <ChevronRight size={20} />
            </Button>
          </Box>

          <Select
            name="view"
            control={control}
            options={viewOptions}
            doTranslate={true}
            actionsChange={handleViewChange}
            size="small"
            variant="filled"
            backgroundColor={theme.palette.background.paperSecondary}
            sx={{
              width: 120,
              height: 40,
              "& .MuiSelect-select": {
                py: 0,
                px: 1.5,
                fontSize: "0.85rem",
                height: "40px",
                display: "flex",
                alignItems: "center",
                minHeight: "unset",
              },
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& .MuiInputBase-root": { height: "100%", minHeight: "unset" },
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 2,
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {/* Navigation */}
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Button
          onClick={goToBack}
          variant="contained"
          color="secondary"
          size="small"
          disabled={!canGoBack}
          ariaLabel={t(CALENDAR_PREVIOUS)}
          sx={{
            minWidth: "auto",
            height: "auto",
            py: "6px",
            px: "10px !important",
            opacity: canGoBack ? 1 : 0.5,
            cursor: canGoBack ? "pointer" : "not-allowed",
          }}
        >
          <ChevronLeft size={20} />
        </Button>
        <Button
          onClick={goToToday}
          variant="contained"
          color="secondary"
          size="small"
          sx={{ minWidth: "auto", height: "auto", py: "6px", px: "16px" }}
        >
          {t(CALENDAR_TODAY)}
        </Button>
        <Button
          onClick={goToNext}
          variant="contained"
          color="secondary"
          size="small"
          ariaLabel={t(CALENDAR_NEXT)}
          sx={{
            minWidth: "auto",
            height: "auto",
            py: "6px",
            px: "10px !important",
          }}
        >
          <ChevronRight size={20} />
        </Button>
      </Box>

      {/* Title (month/year current) */}
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {capitalizeWords(toolbar.label)}
      </Typography>

      {/* Views */}
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Button
          onClick={goToView(Views.MONTH)}
          variant="contained"
          color={toolbar.view === "month" ? "primary" : "secondary"}
          size="small"
          sx={{ minWidth: "auto", height: "auto", py: "6px", px: "16px" }}
        >
          {t(CALENDAR_MONTH)}
        </Button>
        <Button
          onClick={goToView(Views.WEEK)}
          variant="contained"
          color={toolbar.view === "week" ? "primary" : "secondary"}
          size="small"
          sx={{ minWidth: "auto", height: "auto", py: "6px", px: "16px" }}
        >
          {t(CALENDAR_WEEK)}
        </Button>
        <Button
          onClick={goToView(Views.DAY)}
          variant="contained"
          color={toolbar.view === "day" ? "primary" : "secondary"}
          size="small"
          sx={{ minWidth: "auto", height: "auto", py: "6px", px: "16px" }}
        >
          {t(CALENDAR_DAY)}
        </Button>
      </Box>
    </Box>
  );
};

export default CalendarToolbar;
